/**
 * PbxApiKey — vendor PBX credentials managed via Admin → PBX API Keys.
 *
 * Storage:
 *   - `encrypted_secrets` (server-only) is envelope-encrypted JSON whose
 *     shape depends on `kind`:
 *       s2s      → { user: string, pass: string }
 *       oauth2   → { client_id: string, client_secret: string, token_url?: string }
 *       api_key  → { api_key: string }
 *   - `fingerprint` is the last-4 of the principal secret value (the pass,
 *     the client_secret, or the api_key). Returned in API responses so the
 *     admin UI can show "**** abcd" without ever round-tripping the secret.
 */
import { z } from 'zod';
export const PbxApiKeyProviderSchema = z.enum(['wildix', '3cx', 'netsapiens', 'other']);
export const PbxApiKeyKindSchema = z.enum(['s2s', 'oauth2', 'api_key']);
export const PbxApiKeySchema = z.object({
    id: z.string().regex(/^pak_[A-Za-z0-9_-]+$/),
    customer_id: z.string().regex(/^cust_[A-Za-z0-9_-]+$/),
    provider: PbxApiKeyProviderSchema,
    kind: PbxApiKeyKindSchema,
    label: z.string().min(1).max(120),
    fingerprint: z.string(),
    scopes: z.array(z.string()).default([]),
    last_used_at: z.string().datetime().nullable(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});
export const PbxApiKeySecretsSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('s2s'),
        user: z.string().min(1),
        pass: z.string().min(1),
    }),
    z.object({
        kind: z.literal('oauth2'),
        client_id: z.string().min(1),
        client_secret: z.string().min(1),
        token_url: z.string().url().optional(),
    }),
    z.object({
        kind: z.literal('api_key'),
        api_key: z.string().min(1),
    }),
]);
export const PbxApiKeyCreateSchema = z.object({
    provider: PbxApiKeyProviderSchema,
    label: z.string().min(1).max(120),
    scopes: z.array(z.string()).default([]),
    secrets: PbxApiKeySecretsSchema,
});
export const PbxApiKeyUpdateSchema = z.object({
    label: z.string().min(1).max(120).optional(),
    scopes: z.array(z.string()).optional(),
    /** Optional rotation — omit to keep existing secrets. */
    secrets: PbxApiKeySecretsSchema.optional(),
});
export function fingerprintFor(secrets) {
    const principal = secrets.kind === 's2s' ? secrets.pass :
        secrets.kind === 'oauth2' ? secrets.client_secret :
            secrets.api_key;
    return principal.length <= 4 ? '****' : `****${principal.slice(-4)}`;
}
//# sourceMappingURL=pbx-api-keys.js.map